import { Component, OnInit, AfterViewInit, ChangeDetectorRef, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ShipService } from 'src/app/services/ship/ship.service';
import { ShipModalService } from 'src/app/services/ship-modal/ship-modal.service';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-ship-listing',
  templateUrl: './ship-listing.component.html',
  styleUrls: ['./ship-listing.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class ShipListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() shipAdded = new EventEmitter<void>();
  @Output() shipUpdated = new EventEmitter<void>();

  // Data properties
  ships: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    search: '',
    date_from: '',
    date_to: ''
  };



  constructor(
    private _shipService: ShipService,
    private _shipModalService: ShipModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadShips();
    this.setupModalSubscriptions();
  }

  ngAfterViewInit(): void {
    // Initialize menus after view is initialized
    this.initializeMenus();
  }



  private setupModalSubscriptions(): void {
    // No specific modal subscriptions needed for this component
    // The modal events are handled by the parent component
  }

  loadShips(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;

    this.subscriptions.push(
      this._shipService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.ships = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.ships = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading ships:', error);
          this.ships = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadShips(page, this.searchTerm, this.filters);
  }



  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadShips(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: ''
    };
    this.searchTerm = '';
    this.loadShips(1, '', {});
  }

  openAddShipModal(): void {
    this._shipModalService.clearSelectedShip();
    const modalElement = document.getElementById('kt_modal_ship_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditShipModal(ship: any): void {
    this._shipModalService.setSelectedShip(ship);
    const modalElement = document.getElementById('kt_modal_ship_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteShip(ship: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ship "${ship.name || ship.ship_name || 'this ship'}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(ship);
      }
    });
  }

  private performDelete(ship: any): void {
    this.subscriptions.push(
      this._shipService.DeleteDataById(ship._id || ship.id).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Ship has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            });
            this.loadShips(this.currentPage, this.searchTerm, this.filters);
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete ship.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error) => {
          console.error('Error deleting ship:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete ship. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onShipAdded(): void {
    console.log('Ship added, refreshing list...');
    this.loadShips(this.currentPage, this.searchTerm, this.filters);
    this.shipAdded.emit();
  }

  onShipUpdated(): void {
    console.log('Ship updated, refreshing list...');
    this.loadShips(this.currentPage, this.searchTerm, this.filters);
    this.shipUpdated.emit();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Initialize KTMenu instances
  private initializeMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 100);
  }

  // Refresh menus after data load
  private refreshMenus(): void {
    setTimeout(() => {
      if (typeof KTMenu !== 'undefined') {
        KTMenu.createInstances();
      }
    }, 50);
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }
}
